import {
  Component,
  HostBinding,
  OnInit,
  OnDestroy,
  Inject
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { find } from 'lodash';
import {
  WebsocketGatewayService,
  EnvironmentConfig,
  ENVIRONMENT_CONFIG
} from '@ipreo/northstar';
import moment, { Moment } from 'moment';
import { MuniEnvironment, MuniEnvironmentParams } from '@ipreo/muni-shared';
import { Event } from '../../../models/calendar';
import { CalendarState } from '../../../state/calendar/reducer';
import {
  CalendarLoadAction,
  CalendarLoadSuccessAction
} from '../../../state/calendar/actions';
import { CalendarService } from '../../../services/calendar.service';
import {
  getOpenState,
  getCalendarState,
  getDealDate,
  getLiveState,
  MarketDrawerRootReducerState
} from '../../../state/reducer';

function nonClickable(event: Event): boolean {
  return (
    !(event.highlights && event.highlights.length) &&
    !(event.description && event.description.length) &&
    !event.values.length
  );
}

@Component({
  selector: 'muni-gameday-market-drawer-calendar',
  templateUrl: './calendar.template.html',
  styleUrls: ['./calendar.styles.scss'],
  standalone: false,
})
export class CalendarComponent implements OnInit, OnDestroy {
  @HostBinding('class.marketDrawerSection')
  section = true;

  public today = new Date();
  public readonly open: Observable<boolean> = this.store.pipe(
    select(getOpenState),
    distinctUntilChanged()
  );
  public readonly live: Observable<boolean> = this.store.pipe(
    select(getLiveState),
    distinctUntilChanged()
  );
  public readonly calendarState: Observable<CalendarState> = this.store.pipe(
    select(getCalendarState),
    distinctUntilChanged()
  );
  public readonly loading: Observable<boolean> = this.calendarState.pipe(
    map(s => s.loading),
    distinctUntilChanged()
  );
  public readonly error: Observable<string> = this.calendarState.pipe(
    map(s => s.error),
    distinctUntilChanged()
  );
  public readonly events: Observable<Event[]> = this.calendarState.pipe(
    map(s => s.events.filter(event => !nonClickable(event))),
    distinctUntilChanged()
  );
  public readonly eventsDate: Observable<Date> = this.calendarState.pipe(
    map(s => s.eventsDate),
    distinctUntilChanged()
  );
  public readonly mostRecentEventId: Observable<
    number
  > = this.calendarState.pipe(map(s => s.mostRecentEventId));
  public readonly dealDate: Observable<Moment> = this.store.pipe(
    map(state => moment(getDealDate(state)))
  );
  public readonly dates: Observable<Moment[]> = this.dealDate.pipe(
    map(this.calendarService.calculateDates.bind(this.calendarService))
  );
  public readonly selectedEvent: Observable<Event> = this.calendarState.pipe(
    map(s => {
      if (s.selectedEvent && s.events.length) {
        return find(s.events, event => event.id === s.selectedEvent);
      } else {
        return undefined;
      }
    })
  );

  private liveSubscription: Subscription;
  private env: MuniEnvironment;

  constructor(
    private store: Store<MarketDrawerRootReducerState>,
    private wsgGateway: WebsocketGatewayService,
    private calendarService: CalendarService,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.env = (config.params as MuniEnvironmentParams).env;
  }

  ngOnInit(): void {
    this.connectToChannel();
  }

  ngOnDestroy(): void {
    this.liveSubscription.unsubscribe();
  }

  public setSelectedDate(date: Moment): void {
    this.store.dispatch(new CalendarLoadAction(date.toDate()));
  }

  public letterForDay(date: Moment): string {
    switch (date.format('dd')) {
      case 'Th':
        return 'H';
      default:
        return date.format('dd').charAt(0);
    }
  }

  private connectToChannel(): void {
    this.liveSubscription = this.wsgGateway
      .subscribe(`munis-economic-events-${this.env}`)
      .subscribe((event: any) => {
        const events = this.calendarService.eventsToViewModel(event);
        this.store.dispatch(new CalendarLoadSuccessAction(events));
      });
  }
}
